import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ListChecks, FileUp, CalendarRange, BadgeCheck, Briefcase, Building2, Users, Settings, Search, Bell, LogOut, Menu, AlertTriangle, Clock, Network, ScrollText, Boxes, PackageX, Truck, Landmark, IdCard, Target, HelpCircle, FolderLock, ShieldCheck, UploadCloud, FileDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api } from "@/lib/api";
import { ROLES } from "@/lib/format";
import { Logo } from "@/components/Common";
import { applyTheme, DEFAULT_ALL } from "@/lib/theme";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { WelcomeGuide } from "@/components/WelcomeGuide";
import { TutorialsDialog } from "@/components/TutorialsDialog";
import { SerialHistoryDialog, useSerialHits } from "@/components/SerialLookup";

const NAV = [
  { to: "/", label: "Centro operativo", icon: LayoutDashboard, id: "dashboard", perm: "orders:read" },
  { to: "/ordini", label: "Ordini e ticket", icon: ListChecks, id: "orders", perm: "orders:read" },
  { to: "/importazione", label: "Importazione ordini", icon: FileUp, id: "import", perm: "import:run" },
  { to: "/pianificazione", label: "Pianificazione", icon: CalendarRange, id: "planning", perm: "orders:read" },
  { to: "/produzione", label: "Controllo produzione", icon: BadgeCheck, id: "production", badge: true, perm: "orders:read" },
  { to: "/commesse", label: "Commesse e listini", icon: Briefcase, id: "projects", perm: "commercial:read" },
  { to: "/risorse", label: "Risorse e squadre", icon: Users, id: "resources", perm: "hr:read" },
  { to: "/magazzino", label: "Magazzino", icon: Boxes, id: "warehouse", perm: "inventory:read" },
  { to: "/flotta", label: "Flotta e attrezzature", icon: Truck, id: "fleet", perm: "fleet:read" },
  { to: "/amministrazione", label: "Amministrazione", icon: Landmark, id: "accounting", perm: "accounting:read" },
  { to: "/personale", label: "Personale", icon: IdCard, id: "personnel", perm: "hr:read" },
  { to: "/controllo-gestione", label: "Controllo di gestione", icon: Target, id: "control", perm: "finance:read" },
  { to: "/documentale", label: "Documentale", icon: FolderLock, id: "documents", perm: "docs:read" },
  { to: "/anagrafiche", label: "Anagrafiche", icon: Building2, id: "registry", perm: "orders:read" },
  { to: "/gruppo", label: "Configurazione gruppo", icon: Network, id: "group", perm: "group:manage" },
  { to: "/audit", label: "Registro modifiche", icon: ScrollText, id: "audit", perm: "audit:read" },
  { to: "/sicurezza", label: "Sicurezza e privacy", icon: ShieldCheck, id: "security", perm: "group:manage" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "settings", perm: "group:manage" },
];

const SideNav = ({ stats, onNavigate }) => {
  const { can } = useAuth();
  return (
    <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3">
      {NAV.filter((n) => can(n.perm)).map((n) => (
        <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={onNavigate} data-testid={`nav-${n.id}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-white text-[#0B0F14] font-semibold" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}>
          <n.icon className="h-4 w-4" />
          <span className="flex-1">{n.label}</span>
          {n.badge && stats?.to_validate > 0 && (
            <span className="rounded-sm bg-urgent px-1.5 font-mono text-[11px] font-semibold text-white">{stats.to_validate}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );
};

const Sidebar = ({ stats, onNavigate }) => (
  <div className="relative flex h-full flex-col bg-[#0B0F14] text-white grain">
    <div className="brand-stripe h-1 w-full" />
    <div className="px-6 pb-8 pt-7">
      <Logo className="h-9" />
      <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500">ERP di gruppo · Telecom</div>
    </div>
    <SideNav stats={stats} onNavigate={onNavigate} />
    <div className="px-6 py-5 text-[11px] text-zinc-600">v2.0 · Delivery, Assurance &amp; Construction</div>
  </div>
);

const MY_ICON = { documents: FolderLock, fleet: Truck, hr: IdCard, privacy: ShieldCheck, imports: UploadCloud, warehouse: PackageX, accounting: Landmark };

const Notifications = ({ stats }) => {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const location = useLocation();
  const [stock, setStock] = useState(null);
  const [fleet, setFleet] = useState(null);
  const [acc, setAcc] = useState(null);
  const [hr, setHr] = useState(null);
  const [budget, setBudget] = useState(null);
  const [invites, setInvites] = useState(null);
  const [docs, setDocs] = useState(null);
  const [myDocs, setMyDocs] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const loadMine = () => api.get("/my-alerts").then((r) => setMyDocs(r.data)).catch(() => {});
  const [bellOpen, setBellOpen] = useState(false);
  useEffect(() => {
    if (can("docs:read")) api.get("/documents/alerts").then((r) => setDocs(r.data)).catch(() => {});
    loadMine();
    if (can("finance:read")) api.get("/control/alerts").then((r) => setBudget(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/users/invite-alerts").then((r) => setInvites(r.data)).catch(() => {});
    if (can("hr:read")) api.get("/personnel/alerts").then((r) => setHr(r.data)).catch(() => {});
    if (can("accounting:read")) api.get("/accounting/alerts").then((r) => setAcc(r.data)).catch(() => {});
    if (can("inventory:read")) api.get("/warehouse/alerts").then((r) => setStock(r.data)).catch(() => {});
    if (can("fleet:read")) api.get("/fleet/alerts").then((r) => setFleet(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, location.pathname]);
  const items = [
    invites?.expired > 0 && { icon: Users, tone: "text-red-600", text: invites.expired === 1 ? "1 invito scaduto: la persona non ha ancora scelto la password" : `${invites.expired} inviti scaduti: password non ancora scelte`, to: "/gruppo?tab=users" },
    docs?.expired > 0 && { icon: FolderLock, tone: "text-red-600", text: docs.expired === 1 ? "1 documento scaduto o in scadenza oggi (contratti, certificazioni, procedure)" : `${docs.expired} documenti scaduti o in scadenza oggi (contratti, certificazioni, procedure)`, to: "/documentale" },
    docs?.soon > 0 && { icon: FolderLock, tone: "text-urgent", text: docs.soon === 1 ? "1 documento in scadenza entro 30 giorni" : `${docs.soon} documenti in scadenza entro 30 giorni`, to: "/documentale" },
    budget?.over > 0 && { icon: Target, tone: "text-red-600", text: `${budget.over} budget con costi oltre il 100% a oggi`, to: "/controllo-gestione" },
    budget?.warn > 0 && { icon: Target, tone: "text-urgent", text: `${budget.warn} budget con costi oltre il 90% a oggi`, to: "/controllo-gestione" },
    hr?.overdue > 0 && { icon: IdCard, tone: "text-red-600", text: `${hr.overdue} scadenze del personale superate (contratti, abilitazioni, visite, documenti)`, to: "/personale" },
    hr?.soon > 0 && { icon: IdCard, tone: "text-urgent", text: `${hr.soon} scadenze del personale entro 30 giorni`, to: "/personale" },
    acc?.OUT.overdue > 0 && { icon: Landmark, tone: "text-red-600", text: `${acc.OUT.overdue} scadenze clienti non incassate (${acc.OUT.overdue_amount.toLocaleString("it-IT", { style: "currency", currency: "EUR" })})`, to: "/amministrazione" },
    acc?.IN.overdue > 0 && { icon: Landmark, tone: "text-urgent", text: `${acc.IN.overdue} pagamenti a fornitori scaduti (${acc.IN.overdue_amount.toLocaleString("it-IT", { style: "currency", currency: "EUR" })})`, to: "/amministrazione" },
    fleet?.overdue > 0 && { icon: Truck, tone: "text-red-600", text: `${fleet.overdue} scadenze flotta superate o in scadenza oggi`, to: "/flotta" },
    fleet?.d7 + fleet?.d30 > 0 && { icon: Truck, tone: "text-urgent", text: `${fleet.d7} scadenze flotta entro 7 giorni, ${fleet.d30} entro 30`, to: "/flotta" },
    stock?.negative > 0 && { icon: PackageX, tone: "text-red-600", text: `${stock.negative} giacenze di magazzino negative`, to: "/magazzino" },
    stock?.low > 0 && { icon: Boxes, tone: "text-urgent", text: `${stock.low} articoli sotto scorta minima`, to: "/magazzino" },
    stock?.anomalies > 0 && { icon: AlertTriangle, tone: "text-red-600", text: `${stock.anomalies} anomalie seriali da verificare in magazzino`, to: "/magazzino" },
    stats?.sla_breach > 0 && { icon: AlertTriangle, tone: "text-red-600", text: `${stats.sla_breach} ticket con SLA scaduto`, to: "/ordini?service_type=ASSURANCE" },
    stats?.urgent > 0 && { icon: Clock, tone: "text-urgent", text: `${stats.urgent} ordini urgenti o in scadenza`, to: "/ordini" },
    stats?.received > 0 && { icon: ListChecks, tone: "text-slate-600", text: `${stats.received} ordini ricevuti da verificare`, to: "/ordini?status=RECEIVED" },
    stats?.to_validate > 0 && { icon: BadgeCheck, tone: "text-primary", text: `${stats.to_validate} interventi da validare`, to: "/produzione" },
  ].filter(Boolean);
  const unread = myDocs.filter((n) => !n.read).length;
  const openDoc = async (n) => { setBellOpen(false); if (!n.read) await api.post("/my-alerts/read", { key: n.key }).catch(() => {}); loadMine(); navigate(n.link); };
  const readAll = async () => { await api.post("/my-alerts/read", {}).catch(() => {}); loadMine(); };
  return (
    <Popover open={bellOpen} onOpenChange={setBellOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-[hsl(var(--shell-fg))] hover:bg-[hsl(var(--shell-fg)/0.12)] hover:text-[hsl(var(--shell-fg))]" data-testid="topbar-notifications-button">
          <Bell className="h-5 w-5" />
          {(items.length > 0 || unread > 0) && <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-urgent" data-testid="bell-dot" />}
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 font-mono text-[10px] font-bold leading-[18px] text-white" data-testid="bell-unread-count">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[80vh] w-96 overflow-y-auto p-0" data-testid="notifications-panel">
        {myDocs.length > 0 && <>
          <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-3"><span className="micro-label">I tuoi avvisi</span>
            {unread > 0 && <button onClick={readAll} className="text-xs font-semibold text-primary hover:underline" data-testid="bell-read-all">Segna tutti come letti</button>}</div>
          {(showAll ? myDocs : myDocs.slice(0, 8)).map((n) => { const Icon = MY_ICON[n.kind]; return (
            <button key={n.key} onClick={() => openDoc(n)} data-testid={`bell-my-${n.key}`} className={`flex w-full items-start gap-3 border-b px-4 py-3 text-left text-sm transition-colors hover:bg-muted ${n.read ? "text-muted-foreground" : ""}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : n.tone === "danger" ? "bg-red-600" : "bg-urgent"}`} />
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span className={n.read ? "" : "font-semibold"}>{n.text}</span>
            </button>
          ); })}
          {myDocs.length > 8 && <button onClick={() => setShowAll((s) => !s)} className="w-full border-b px-4 py-2 text-center text-xs font-semibold text-primary hover:bg-muted" data-testid="bell-show-all">{showAll ? "Mostra meno" : `Mostra tutti (${myDocs.length})`}</button>}
        </>}
        <div className="border-b px-4 py-3 micro-label">Notifiche</div>
        {items.length === 0 && <div className="p-4 text-sm text-muted-foreground">Nessuna notifica</div>}
        {items.map((it, i) => (
          <button key={i} onClick={() => { setBellOpen(false); navigate(it.to); }} data-testid={`bell-item-${i}`} className="flex w-full items-center gap-3 border-b px-4 py-3 text-left text-sm transition-colors hover:bg-muted">
            <it.icon className={`h-4 w-4 ${it.tone}`} />{it.text}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const TopSearch = () => {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const [serialId, setSerialId] = useState("");
  const close = useCallback(() => setSerialId(""), []);
  const navigate = useNavigate();
  const hits = useSerialHits(q, can("inventory:read"));
  const open = (s) => { setSerialId(s.id); setQ(""); };
  const submit = (e) => {
    e.preventDefault();
    const exact = hits.filter((s) => s.serial.toUpperCase() === q.trim().toUpperCase());
    if (exact.length === 1) return open(exact[0]);
    navigate(`/ordini?q=${encodeURIComponent(q)}`);
  };
  return (
    <form className="relative hidden max-w-md flex-1 md:block" onSubmit={submit}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 200)}
        placeholder={can("inventory:read") ? "Cerca ordine, ticket, cliente, località, seriale…" : "Cerca ordine, ticket, cliente, località…"} className="bg-white pl-9 text-foreground" data-testid="topbar-search-input" />
      {focus && hits.length > 0 && (
        <div className="absolute z-50 mt-1 w-full border bg-white text-foreground shadow-lg" data-testid="topbar-serial-results">
          <div className="micro-label border-b px-3 py-1.5">Apparati per seriale</div>
          {hits.map((s) => (
            <button key={s.id} type="button" onClick={() => open(s)} className="flex w-full justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent" data-testid={`topbar-serial-${s.serial}`}>
              <span><span className="font-mono text-xs font-semibold">S/N {s.serial}</span> · {s.item_code}</span>
              <span className="truncate text-xs text-muted-foreground">{s.warehouse_name || (s.status === "INSTALLED" ? "Installato" : s.status)}</span>
            </button>
          ))}
        </div>
      )}
      <SerialHistoryDialog serialId={serialId} onClose={close} />
    </form>
  );
};

const Topbar = ({ stats, mobileNav, onGuide, onTutorials }) => {
  const { user, logout } = useAuth();
  const { visibleCompanies, companyId, setCompanyId } = useData();
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-3 bg-[hsl(var(--shell))] px-4 text-[hsl(var(--shell-fg))] shadow-sm transition-colors duration-300 lg:px-8" data-testid="topbar">
      {mobileNav}
      <Select value={companyId || "all"} onValueChange={(v) => setCompanyId(v === "all" ? "" : v)}>
        <SelectTrigger className="w-[220px] bg-white text-foreground" data-testid="company-selector">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{visibleCompanies.length > 1 ? "Tutte le società visibili" : "Società assegnata"}</SelectItem>
          {visibleCompanies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}
        </SelectContent>
      </Select>
      <TopSearch />
      <div className="ml-auto flex items-center gap-1">
        <Notifications stats={stats} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-[hsl(var(--shell-fg)/0.12)]" data-testid="topbar-user-menu">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white font-display text-sm font-bold text-[#0B0F14]">{user.name.slice(0, 1)}</span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-semibold leading-tight">{user.name}</span>
                <span className="block max-w-[260px] truncate text-xs text-[hsl(var(--shell-fg)/0.75)]" data-testid="topbar-user-role">{user.role === "direzione" && !user.group_scope ? "Direzione società" : ROLES[user.role]}{user.department_labels?.length > 0 && ` · ${user.department_labels.join(", ")}`}</span>
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal"><div className="text-sm">{user.email}</div>
              {user.department_labels?.length > 0 && <div className="mt-1 text-xs text-muted-foreground" data-testid="user-menu-departments">{user.department_labels.length > 1 ? "Reparti" : "Reparto"}: {user.department_labels.join(", ")}. Vedi le aree del tuo reparto, più ordini e anagrafiche in lettura.</div>}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onGuide} data-testid="open-welcome-guide"><HelpCircle className="mr-2 h-4 w-4" />Guida alle mie aree</DropdownMenuItem>
            <DropdownMenuItem onClick={onTutorials} data-testid="open-tutorials"><FileDown className="mr-2 h-4 w-4" />Tutorial (PDF)</DropdownMenuItem>
            <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Esci</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default function AppShell() {
  const { companyId, visibleCompanies } = useData();
  const { user, can } = useAuth();
  const [guide, setGuide] = useState(false);
  const [tutorials, setTutorials] = useState(false);
  useEffect(() => { if (user && !user.onboarded_at) setGuide(true); }, [user]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const theme = companyId ? visibleCompanies.find((c) => c.id === companyId)?.theme_color : visibleCompanies.length > 1 ? DEFAULT_ALL : null;

  useEffect(() => {
    applyTheme(theme);
    return () => applyTheme(null);
  }, [theme]);

  useEffect(() => {
    const load = () => api.get("/orders/stats", { params: { company_id: companyId || undefined } }).then((r) => setStats(r.data)).catch(() => {});
    load();
    window.addEventListener("stats:refresh", load);
    return () => window.removeEventListener("stats:refresh", load);
  }, [companyId, location.pathname]);

  const mobileNav = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="text-[hsl(var(--shell-fg))] hover:bg-[hsl(var(--shell-fg)/0.12)] hover:text-[hsl(var(--shell-fg))] lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 border-0 p-0"><Sidebar stats={stats} onNavigate={() => setOpen(false)} /></SheetContent>
    </Sheet>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block"><Sidebar stats={stats} /></aside>
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <Topbar stats={stats} mobileNav={mobileNav} onGuide={() => setGuide(true)} onTutorials={() => setTutorials(true)} />
        <main className="flex-1 px-4 py-8 lg:px-10"><Outlet context={{ stats }} /></main>
        {user && <WelcomeGuide items={NAV.filter((n) => can(n.perm))} open={guide} onClose={() => setGuide(false)} />}
        <TutorialsDialog open={tutorials} onClose={() => setTutorials(false)} />
      </div>
    </div>
  );
}
